import React from "react";
import { Link as GatsbyLink } from "gatsby";
import { ContentSection, HeaderSection } from "../docs/sections";
import { DocPageMeta, DocsTable, Header, Image, InlineMessage } from "../docs";
import { Link, Paragraph, TextContainer, Ul } from "../../lib/src";
import PatternWidgetChart from "../assets/patterns/pattern-widget-chart.png";
import PatternWidgetDescription from "../assets/patterns/pattern-widget-Description.png";
import PatternWidgetForm from "../assets/patterns/pattern-widget-form.png";
import PatternWidgetHighlight from "../assets/patterns/pattern-widget-highlight.png";
import PatternWidgetList from "../assets/patterns/pattern-widget-list.png";
import PatternWidgetRulebuilder from "../assets/patterns/pattern-widget-rulebuilder.png";
import PatternWidgetSidepanel from "../assets/patterns/pattern-widget-sidepanel.png";
import PatternWidgetTable from "../assets/patterns/pattern-widget-table.png";

export const Meta: DocPageMeta = {
	category: "Patterns",
};

export default (): JSX.Element => {
	return (
		<>
			<HeaderSection
				title="Widget"
				subTitle="This guideline offers direction for creating self-contained, customizable widgets that convert data into actionable insights to genuinely inform and effectively engage users."
			/>
			<ContentSection>
				<TextContainer article>
					<Header.H2>Principle</Header.H2>
					<Paragraph>When designing a widget, follow these principles: </Paragraph>
					<Ul
						items={[
							<>
								<strong>Singular focus</strong>: Each widget serves <em>one</em> purpose. Avoid
								combining unrelated data or features. Use clear follow-up actions (links, buttons)
								to provide deeper context when needed.
							</>,
							<>
								<strong>Intuitive clarity</strong>: Widgets should be immediately understandable,
								even in isolation. Include relevant timeframes, avoid jargon, and minimize
								dependencies on external information.
							</>,
							<>
								<strong>Insight-driven</strong>: Prioritize valuable conclusions over raw data.
								Visualize data thoughtfully, favoring charts and diagrams over dense text. Tailor
								widgets for their specific context (dashboard vs. detailed report).
							</>,
						]}
					/>
					<Header.H2>Widget structure</Header.H2>
					<Paragraph>When designing a widget, follow this structure: </Paragraph>
					<Ul
						items={[
							<>
								<strong>Title</strong> (Card Header): Clear, concise, and specific. Accurately
								reflects the widget's content and purpose, even when viewed alone.
							</>,
							<>
								<strong>Export</strong>: Located on the right side of the header, allowing users to
								export data or add the widget to a dashboard.
							</>,
							<>
								<strong>Toolbar</strong> (optional): Located below the header, offering primary
								actions (e.g., "Create"), filtering, and search functionality.
							</>,
							<>
								<strong>Key metrics</strong> (optional): Highlight the most important data points,
								using abbreviated numbers and clear units (e.g., 1.2M). Use tooltips to explain
								complex metrics.
							</>,
							<>
								<strong>Content</strong>: The main area for data visualization, combining charts,
								metrics, and brief text where appropriate. Ensure all graphs within a widget share
								the same time range. Use tabs sparingly to separate related metrics for the same
								item.
							</>,
							<>
								<strong>Footnotes</strong> (optional): Brief contextual notes placed in the
								bottom-left corner. Prioritize in-line tooltips for explanations.
							</>,
							<>
								<strong>Follow-Up Actions</strong> (optional): Consistent across all widgets. A
								maximum of two call-to-action buttons at the bottom, guiding users to the next
								logical step.
							</>,
						]}
					/>
					<Header.H2>Widget type</Header.H2>
					<Paragraph>
						Choose the most suitable widget based on the content, complexity, and user goals:{" "}
						<Link href="https://www.figma.com/design/AhA4uo6mdePUODdc14flIZ/Fancy-UI-kit?node-id=4942-70533&t=tGXYwlO5bBNq68TC-1">
							{" "}
							Link to Figma Widgets
						</Link>
					</Paragraph>
					<DocsTable withImage flexibleHeight>
						<thead>
							<tr>
								<th>Type</th>
								<th>Example</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td>
									<TextContainer>
										<Header.H3>Chart</Header.H3>{" "}
										<Paragraph>
											Ideal for visualizing trends and comparisons. Use line charts, bar charts, pie
											charts, etc., appropriately.
										</Paragraph>
										<Paragraph>
											<strong>Usage</strong>:{" "}
										</Paragraph>
										<Ul
											items={[
												<>Dashboard: Provide an overview of key metrics and trends.</>,
												<>Reports: Visualize data analysis results.</>,
												<>Analytics: Monitor performance and track goals.</>,
											]}
										/>
									</TextContainer>
								</td>
								<td>
									<Image src={PatternWidgetChart} alt="Bar chart" />
								</td>
							</tr>
							<tr>
								<td>
									<TextContainer>
										<Header.H3>List</Header.H3>
										<Paragraph>
											Perfect for displaying items, tasks, or records in a structured way.
										</Paragraph>
										<Paragraph>
											<strong>Usage</strong>:{" "}
										</Paragraph>
										<Ul
											items={[
												<>Task lists: Organize and track tasks or activities.</>,
												<>Data lists: Display records or items in a structured format.</>,
												<>Navigation: Provide links to different sections of the application.</>,
											]}
										/>
									</TextContainer>
								</td>
								<td>
									<Image src={PatternWidgetList} alt="Bar chart" />
								</td>
							</tr>
							<tr>
								<td>
									<TextContainer>
										<Header.H3>Table</Header.H3>
										<Paragraph>
											Suitable for detailed data analysis and comparison. Include filtering and
											sorting options.
										</Paragraph>
										<Paragraph>
											<strong>Usage</strong>:{" "}
										</Paragraph>
										<Ul
											items={[
												<>Data analysis: Explore and analyze complex datasets.</>,
												<>Reporting: Present detailed information in a structured format.</>,
												<>Comparison: Compare different data sets side-by-side.</>,
											]}
										/>
									</TextContainer>
								</td>
								<td>
									{" "}
									<Image src={PatternWidgetTable} alt="Line chart" />
								</td>
							</tr>
							<tr>
								<td>
									<TextContainer>
										<Header.H3>Description</Header.H3>
										<Paragraph>Provides textual information, instructions, or summaries.</Paragraph>
										<Paragraph>
											<strong>Usage</strong>:{" "}
										</Paragraph>
										<Ul
											items={[
												<>Onboarding: Guide new users through the application.</>,
												<>Help: Provide context-sensitive information or troubleshooting tips.</>,
												<>Notifications: Display alerts or updates.</>,
											]}
										/>
									</TextContainer>
								</td>
								<td>
									{" "}
									<Image src={PatternWidgetDescription} alt="Bubble chart" />
								</td>
							</tr>
							<tr>
								<td>
									<TextContainer>
										<Header.H3>Form</Header.H3>
										<Paragraph>
											Facilitates user input. Prioritize clear validation and error handling.
										</Paragraph>
										<Paragraph>
											<strong>Usage</strong>:{" "}
										</Paragraph>
										<Ul
											items={[
												<>Settings: Allow users to customize preferences.</>,
												<>Data Entry: Capture information for forms or surveys.</>,
												<>Feedback: Collect user feedback or suggestions.</>,
											]}
										/>
									</TextContainer>
								</td>
								<td>
									{" "}
									<Image src={PatternWidgetForm} alt="Pie chart" />
								</td>
							</tr>
							<tr>
								<td>
									<TextContainer>
										<Header.H3>Rule builder</Header.H3>
										<Paragraph>Empowers users to create custom conditions or criteria.</Paragraph>
										<Paragraph>
											<strong>Usage</strong>:{" "}
										</Paragraph>
										<Ul
											items={[
												<>
													Policy creation: Craft tailored policies for managing access, permissions,
													or behavior within a system.
												</>,
												<>
													Key metrics setup: Establish custom metrics for measuring specific aspects
													of performance or engagement.
												</>,
												<>
													Funnel configuration: Track user journeys through a series of steps to
													identify bottlenecks or optimization opportunities.
												</>,
											]}
										/>
									</TextContainer>
								</td>
								<td>
									{" "}
									<Image src={PatternWidgetRulebuilder} alt="Matrix chart" />
								</td>
							</tr>
							<tr>
								<td>
									<TextContainer>
										<Header.H3>Highlight</Header.H3>
										<Paragraph>Emphasizes critical information or metrics.</Paragraph>
										<Paragraph>
											<strong>Usage</strong>:{" "}
										</Paragraph>
										<Ul
											items={[
												<>Dashboard: Highlight critical KPIs or performance indicators.</>,
												<>Reports: Draw attention to significant findings or trends.</>,
											]}
										/>
									</TextContainer>
								</td>
								<td>
									{" "}
									<Image src={PatternWidgetHighlight} alt="Flat gauge" />
								</td>
							</tr>
							<tr>
								<td>
									<TextContainer>
										<Header.H3>Side Panel</Header.H3>
										<Paragraph>
											A collapsible panel that can contain any of the above widget types for
											flexible layouts.
										</Paragraph>
										<Paragraph>
											<strong>Usage</strong>:{" "}
										</Paragraph>
										<Ul
											items={[
												<>Navigation: Provide access to different sections of the application.</>,
												<>
													Contextual Information: Display additional details or options related to
													the main content.
												</>,
											]}
										/>
									</TextContainer>
								</td>
								<td>
									{" "}
									<Image src={PatternWidgetSidepanel} alt="Flat gauge" />
								</td>
							</tr>
						</tbody>
					</DocsTable>
					<Header.H2>Guidelines</Header.H2>
					<Header.H3>Do's</Header.H3>
					<InlineMessage variant="best-practices">
						<Ul
							items={[
								<>Prioritize clarity, readability, and visual consistency across widgets.</>,
								<>
									Provide immediate feedback for user interactions (e.g., loading states, hover
									effects).
								</>,
								<>
									Use abbreviated numbers (e.g., 1K, 5M) for metrics and include currency symbols
									with monetary values.
								</>,
							]}
						/>
					</InlineMessage>
					<Header.H3>Don'ts</Header.H3>
					<InlineMessage variant="do-not-use-when">
						<Ul
							items={[
								<>
									Combine unrelated data or functions within a single widget. Each widget should
									have a single, clear purpose.
								</>,
								<>Abbreviate percentages (e.g., use "50%" instead of "50pct").</>,
								<>
									Overwhelm users with excessive information. Prioritize the most important
									insights.
								</>,
								<>Use complex layouts that hinder navigation or understanding.</>,
								<>
									Display empty widgets. Show an{" "}
									<GatsbyLink to="/lab/components/Visuals/Empty State">empty state</GatsbyLink>{" "}
									message or hide the widget if no data is available.
								</>,
								<>Exceed two follow-up actions per widget.</>,
							]}
						/>
					</InlineMessage>
				</TextContainer>
			</ContentSection>
		</>
	);
};
